import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { Eye, EyeOff, Lock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const ADMIN_EMAIL = "nkadimengdanny7@gmail.com";

export const Route = createFileRoute("/reset-password")({
  head: () => ({
    meta: [
      { title: "Reset your password | Nkadimeng Danny Legal & Compliance" },
      {
        name: "description",
        content:
          "Choose a new password for your Nkadimeng Danny client portal account and get straight back to your matters, uploads and documents.",
      },
      { property: "og:title", content: "Reset your client portal password" },
      {
        property: "og:description",
        content:
          "Set a new password for your Nkadimeng Danny client portal account.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (cancelled) return;
      const email = (data.user?.email ?? "").toLowerCase();
      if (email && email === ADMIN_EMAIL) {
        // Admin passwords are never reset through the public recovery flow.
        setBlocked(true);
        await supabase.auth.signOut();
      }
      setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (error) {
      toast.error("Couldn't update your password", { description: error.message });
      return;
    }
    toast.success("Password updated", { description: "You're signed in — welcome back." });
    navigate({ to: "/portal/dashboard" as any });
  }

  return (
    <div className="container-narrow grid min-h-[80vh] place-items-center px-4 py-10 sm:py-16">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-5 shadow-soft sm:p-8">
        <p className="text-[11px] uppercase tracking-[0.3em] text-gold">Client portal</p>
        <h1 className="mt-2 font-display text-2xl text-navy sm:text-3xl">Set a new password</h1>

        {!ready ? (
          <p className="mt-4 text-sm text-muted-foreground">Checking your reset link…</p>
        ) : blocked ? (
          <div className="mt-4 flex items-start gap-2 rounded-md border border-navy/15 bg-navy/5 p-3 text-sm text-navy/80">
            <Lock className="mt-0.5 h-4 w-4 text-navy" />
            <span>
              Admin passwords can&rsquo;t be reset through this flow for security reasons.
              Please sign in with your existing admin credentials.
            </span>
          </div>
        ) : (
          <>
            <p className="mt-2 text-sm text-muted-foreground">
              Choose a password of at least 6 characters. You&rsquo;ll be taken straight to
              your dashboard.
            </p>
            <form onSubmit={onSubmit} className="mt-5 space-y-3">
              <div>
                <Label htmlFor="new-password">New password</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={show ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    autoComplete="new-password"
                    className="h-11 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    aria-label={show ? "Hide password" : "Show password"}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-muted-foreground hover:text-navy"
                  >
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label htmlFor="confirm-password">Confirm password</Label>
                <Input
                  id="confirm-password"
                  type={show ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={6}
                  autoComplete="new-password"
                  className="h-11"
                />
              </div>
              <Button
                type="submit"
                className="h-11 w-full bg-navy text-base hover:bg-navy/90"
                disabled={loading}
              >
                {loading ? "Updating…" : "Update password"}
              </Button>
            </form>
          </>
        )}

        <p className="mt-6 text-center text-xs text-muted-foreground">
          <Link to="/auth" className="hover:text-navy">← Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}
